/* Helpers shared across the mock route modules. */

import type { CareTask, DashboardSummary, Plant } from '@core/types'
import { ApiError } from '../../http'
import { db, nextId } from '../store'
import { DASHBOARD_SUMMARY } from '../data/rewards'

export function addDays(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date.toISOString()
}

export function requirePlant(id: string | undefined): Plant {
  const plant = db.plants.find((item) => item.id === id)
  if (!plant) {
    throw new ApiError({
      status: 404,
      code: 'plant_not_found',
      message: 'That plant is no longer in your garden. It may have been removed from another device.',
    })
  }
  return plant
}

/** Single place where points are written, so balance and history never disagree. */
export function awardPoints(points: number, label: string, source: string): void {
  db.rewardAccount.pointsBalance += points
  if (points > 0) db.rewardAccount.lifetimePoints += points
  db.rewardAccount.history.unshift({
    id: nextId('pt', 900),
    label,
    points,
    occurredAt: new Date().toISOString(),
    source: source as never,
  })
}

export function byUrgency(a: CareTask, b: CareTask): number {
  const rank = { overdue: 0, due: 1, upcoming: 2, completed: 3 }
  return rank[a.status] - rank[b.status] || new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
}

export function healthRank(health: Plant['health']): number {
  return { 'at-risk': 3, 'needs-water': 2, thriving: 1, lost: 0 }[health]
}

export function addDaysToIso(source: Date, days: number): string {
  const date = new Date(source)
  date.setDate(date.getDate() + days)
  return date.toISOString()
}

export function readQueryNumber(query: URLSearchParams, key: string): number | undefined {
  const raw = query.get(key)
  if (!raw) return undefined
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * The dashboard is derived from the store, so KPIs can never contradict the
 * list pages the user is looking at.
 */
export function buildDashboardSummary(): DashboardSummary {
  const plants = db.plants
  const alive = plants.filter((plant) => plant.health !== 'lost')

  return {
    plants: {
      total: plants.length,
      thriving: plants.filter((plant) => plant.health === 'thriving').length,
      needsWater: plants.filter((plant) => plant.health === 'needs-water').length,
      atRisk: plants.filter((plant) => plant.health === 'at-risk').length,
      lost: plants.filter((plant) => plant.health === 'lost').length,
    },
    survivalRate: plants.length === 0 ? 0 : alive.length / plants.length,
    verifiedActions: 47 + plants.length,
    pointsBalance: db.rewardAccount.pointsBalance,
    streakDays: db.rewardAccount.currentStreakDays,
    monthlyImpact: DASHBOARD_SUMMARY.monthlyImpact,
  }
}

export const EMPTY_DASHBOARD: DashboardSummary = {
  plants: { total: 0, thriving: 0, needsWater: 0, atRisk: 0, lost: 0 },
  survivalRate: 0,
  verifiedActions: 0,
  pointsBalance: 0,
  streakDays: 0,
  monthlyImpact: [],
}
